import { isEqual } from 'lodash-es';

import type {
  ActionRow,
  ListboxSelection,
  Option,
  OptionGroup,
  SelectOptions,
} from './types';

export function isMultiSelect<P extends { multiple?: boolean }>(
  props: P,
): props is Extract<P, { multiple: true }> {
  return props.multiple === true;
}

function isBoxedPrimitive(value: unknown) {
  return (
    value instanceof Number ||
    value instanceof String ||
    value instanceof Boolean ||
    value instanceof Symbol
  );
}

function unboxValue<V>(value: V): unknown {
  return isBoxedPrimitive(value)
    ? (value as { valueOf(): unknown }).valueOf()
    : value;
}

function hasOnlyDataProperties(value: object) {
  return Object.values(Object.getOwnPropertyDescriptors(value)).every(
    (descriptor) => !descriptor.get && !descriptor.set,
  );
}

function isKeyableValue(value: unknown) {
  if (value === null || value === undefined) return true;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }
  if (typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype === Date.prototype) {
    return !Object.prototype.hasOwnProperty.call(value, 'toJSON');
  }
  if (
    prototype !== Object.prototype &&
    prototype !== Array.prototype &&
    prototype !== null
  ) {
    return false;
  }
  return !('toJSON' in value) && hasOnlyDataProperties(value);
}

function toEqualityKeyPart(this: unknown, key: string, value: unknown) {
  const original = (this as Record<string, unknown>)[key];
  if (!isKeyableValue(original)) {
    throw new TypeError('Value has no equality-preserving JSON key');
  }
  if (
    typeof original !== 'object' ||
    original === null ||
    Array.isArray(original) ||
    original instanceof Date
  ) {
    return value;
  }
  const record = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(record)
      .sort()
      .map((recordKey) => [recordKey, record[recordKey]]),
  );
}

function getObjectValueKey(value: object) {
  try {
    return JSON.stringify(value, toEqualityKeyPart);
  } catch {
    return null;
  }
}

/**
 * Membership test against `values` with lodash `isEqual` semantics, built once
 * so checking plain data (primitives, plain objects, arrays, Dates) costs a
 * lookup rather than a deep compare per value. Any other shape is compared
 * with `isEqual` directly.
 */
export function createValueLookup<V>(values: V[]) {
  const primitives = new Set<unknown>();
  const objects: V[] = [];
  const objectsByKey = new Map<string, V[]>();
  const unkeyedObjects: V[] = [];

  values.forEach((value) => {
    const unboxed = unboxValue(value);
    if (typeof unboxed !== 'object' || unboxed === null) {
      primitives.add(unboxed);
      return;
    }
    objects.push(value);
    const key = getObjectValueKey(unboxed);
    if (key === null) {
      unkeyedObjects.push(value);
      return;
    }
    const bucket = objectsByKey.get(key);
    if (bucket) {
      bucket.push(value);
    } else {
      objectsByKey.set(key, [value]);
    }
  });

  return (value: V) => {
    const unboxed = unboxValue(value);
    if (typeof unboxed !== 'object' || unboxed === null) {
      return primitives.has(unboxed);
    }
    const key = getObjectValueKey(unboxed);
    const candidates =
      key === null
        ? objects
        : [...(objectsByKey.get(key) ?? []), ...unkeyedObjects];
    return candidates.some((candidate) => isEqual(candidate, value));
  };
}

/**
 * `useListNavigation`'s `disabledIndices`, read off the rows rather than the
 * DOM: leading rows, then options, then footer rows. An index with no row is
 * disabled, as an index with no element is to Floating UI.
 */
export function getListboxDisabledIndices<V>({
  footerRows,
  leadingRows = [],
  options,
}: {
  footerRows: ActionRow[];
  options: Option<V>[];
  leadingRows?: ActionRow[];
}) {
  return (index: number) => {
    const leadingRow = leadingRows[index];
    if (leadingRow) return Boolean(leadingRow.isLoading);
    const option = options[index - leadingRows.length];
    if (option) return Boolean(option.disabled);
    const footerRow = footerRows[index - leadingRows.length - options.length];
    return !footerRow || Boolean(footerRow.isLoading);
  };
}

/**
 * Keeps a click on a row from moving DOM focus off the control that owns
 * virtual focus, such as the combobox input.
 */
export function preventFocusSteal(event: { preventDefault: () => void }) {
  event.preventDefault();
}

function isShallowEqual(a: object, b: object) {
  const aEntries = Object.entries(a);
  return (
    aEntries.length === Object.keys(b).length &&
    aEntries.every(([key, value]) =>
      Object.is(value, (b as Record<string, unknown>)[key]),
    )
  );
}

/**
 * `memo` comparison for listbox rows: shallow, except `__private`, which is
 * compared one level deep so per-row private values don't defeat the memo.
 */
export function areRowPropsEqual<P extends { __private: object }>(
  prev: Readonly<P>,
  next: Readonly<P>,
) {
  const { __private: prevPrivate, ...prevRest } = prev;
  const { __private: nextPrivate, ...nextRest } = next;
  return (
    isShallowEqual(prevRest, nextRest) &&
    isShallowEqual(prevPrivate, nextPrivate)
  );
}

/** Selected values as a flat list, whichever selection mode is in play. */
export function getSelectedValues<V>(props: ListboxSelection<V>): V[] {
  if (isMultiSelect(props)) return props.value;
  if (props.value == null) return [];
  return [props.value];
}

export function findOptionByValue<V>(options: Option<V>[], value: V) {
  return options.find((option) => isEqual(option.value, value));
}

/**
 * Options for the given values, preferring the list in hand and falling back
 * to the last known ones. Values neither list can account for are dropped.
 */
export function resolveSelectedOptions<V>(
  options: Option<V>[],
  lastKnown: Option<V>[],
  values: V[],
) {
  return values
    .map(
      (value) =>
        findOptionByValue(options, value) ??
        findOptionByValue(lastKnown, value),
    )
    .filter((option): option is Option<V> => Boolean(option));
}

export function isGroupedOptions<V>(
  options: SelectOptions<V>,
): options is OptionGroup<V>[] {
  return options.length > 0 && 'options' in options[0];
}

/** Trim, fold case, and strip combining marks so `café` and `cafe` compare equal. */
export function normalizeSearchText(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * The predicate the menu filters with, exported for callers filtering their own
 * options under `onQueryChange`. `secondaryText` matches as well as `text`: it
 * carries what tells two same-named options apart, such as an email.
 */
export function matchesQuery<V>(query: string) {
  const normalized = normalizeSearchText(query);

  return (option: Option<V>) =>
    !normalized ||
    normalizeSearchText(option.text).includes(normalized) ||
    Boolean(
      option.secondaryText &&
      normalizeSearchText(option.secondaryText).includes(normalized),
    );
}

/**
 * Filters within each group and drops the ones left empty, so a query never
 * leaves a heading standing over nothing.
 */
export function filterOptionGroups<V>(
  options: SelectOptions<V>,
  query: string,
): OptionGroup<V>[] {
  const matches = matchesQuery<V>(query);

  // A flat list is one unheaded group: the rest of the component then walks a
  // single shape, and an untitled group renders no heading.
  if (!isGroupedOptions(options)) {
    return [{ id: '', options: options.filter(matches), text: '' }];
  }

  return options
    .map((group) => ({ ...group, options: group.options.filter(matches) }))
    .filter((group) => group.options.length > 0);
}

/** Every option in a list, grouped or not, in render order. */
export function flattenOptions<V>(options: SelectOptions<V>): Option<V>[] {
  return isGroupedOptions(options)
    ? options.flatMap((group) => group.options)
    : options;
}

/**
 * Options in render order. Keyboard navigation and the active index are flat,
 * so they index into this rather than into the groups.
 */
export function flattenOptionGroups<V>(groups: OptionGroup<V>[]): Option<V>[] {
  return groups.flatMap((group) => group.options);
}

/**
 * String form of an option's value for the native `<input type="hidden">`
 * form fallback. `option.id` identifies the option (React keys / ARIA) but
 * isn't necessarily related to `value`, so the submitted data has to be
 * derived from `value` itself, not `id`.
 */
export function stringifyOptionValue<V>(value: V): string {
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
